import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "step", "stepIndicator", "priceDisplay", "quoteSection", "bookButton",
    "dwellingType", "squareFootage", "addonCheckbox", "squareFootageSection",
    "calendarContainer", "timeSlotContainer", "selectedDate", "selectedSlot",
    "propertyForm", "customerForm", "summaryContent",
    "nextButton", "backButton"
  ]

  static values = {
    step: { type: Number, default: 1 },
    dwellingType: String,
    squareFootage: Number,
    addons: { type: Array, default: [] },
    scheduledDate: String,
    timeSlot: String,
    basePrice: Number,
    totalPrice: Number
  }

  // Pricing is loaded from the server API — no hardcoded fallbacks
  pricing = {}
  addonPrices = {}

  async connect() {
    await this.loadPricing()
    this.updateStepDisplay()
  }

  async loadPricing() {
    try {
      const response = await fetch('/admin/pricing_settings/api_pricing')
      const data = await response.json()

      // Update pricing from API
      if (data.tiers) {
        this.pricing = {}
        for (const [dwellingType, tiers] of Object.entries(data.tiers)) {
          this.pricing[dwellingType] = { ranges: tiers }
        }
      }

      // Update addon prices
      if (data.addons) {
        this.addonPrices = data.addons
      }
    } catch (error) {
      console.error('Failed to load pricing from server:', error)
      // Show an error to the user if pricing cannot be loaded
      if (this.hasQuoteSectionTarget) {
        this.quoteSectionTarget.innerHTML = '<p class="text-red-600">Unable to load pricing. Please refresh the page or contact us.</p>'
      }
    }
  }

  // Step 1: Property Type & Square Footage
  selectDwellingType(event) {
    this.dwellingTypeValue = event.currentTarget.value
    this.highlightSelection(event.currentTarget, "dwelling")

    // Show square footage section
    if (this.hasSquareFootageSectionTarget) {
      this.squareFootageSectionTarget.classList.remove("hidden")
    }

    // Filter square footage options based on dwelling type
    this.filterSquareFootageOptions()

    this.calculateQuote()
  }

  filterSquareFootageOptions() {
    // Define max square footage for each dwelling type based on pricing
    const maxSquareFootage = {
      'condo_townhouse': 5000,
      'single_family': 10000,
      'duplex': 7000,
      'multi_family': 8000
    }

    const maxForType = maxSquareFootage[this.dwellingTypeValue] || 8000
    const allOptions = document.querySelectorAll('[data-selection-group="sqft"] label')

    allOptions.forEach(label => {
      const input = label.querySelector('input[type="radio"]')
      const value = parseInt(input.value)

      if (value <= maxForType) {
        label.classList.remove("hidden")
        label.classList.remove("opacity-50", "pointer-events-none")
        input.disabled = false
      } else {
        label.classList.add("hidden")
        input.disabled = true
        input.checked = false
      }
    })
  }

  selectSquareFootage(event) {
    this.squareFootageValue = parseInt(event.currentTarget.value)
    this.highlightSelection(event.currentTarget, "sqft")
    this.calculateQuote()
  }

  highlightSelection(element, type) {
    const container = element.closest(`[data-selection-group="${type}"]`)
    if (container) {
      container.querySelectorAll("label").forEach(label => {
        label.classList.remove("ring-2", "ring-[#0369a1]", "bg-[#eff6ff]")
      })
      element.closest("label").classList.add("ring-2", "ring-[#0369a1]", "bg-[#eff6ff]")
    }
  }

  calculateQuote() {
    if (!this.dwellingTypeValue || !this.squareFootageValue) return

    const pricing = this.pricing[this.dwellingTypeValue] || this.pricing.condo_townhouse
    if (!pricing) return

    // Find the appropriate price based on square footage using tiered pricing
    let basePrice = pricing.ranges[pricing.ranges.length - 1].price

    for (let range of pricing.ranges) {
      if (this.squareFootageValue <= range.max) {
        basePrice = range.price
        break
      }
    }

    this.basePriceValue = basePrice
    this.updateTotalPrice()
    this.showQuoteSection()
  }

  updateTotalPrice() {
    let total = this.basePriceValue || 0

    // Add addon prices
    this.addonsValue.forEach(addon => {
      total += this.addonPrices[addon] || 0
    })

    this.totalPriceValue = total

    // Update all price displays
    if (this.hasPriceDisplayTarget) {
      this.priceDisplayTargets.forEach(el => {
        el.textContent = `$${total}`
      })
    }
  }

  showQuoteSection() {
    if (this.hasQuoteSectionTarget) {
      this.quoteSectionTarget.classList.remove("hidden")
    }
    if (this.hasBookButtonTarget) {
      this.bookButtonTarget.classList.remove("hidden")
    }
  }

  // Step 2: Add-ons
  toggleAddon(event) {
    const addon = event.currentTarget.value
    const checked = event.currentTarget.checked

    if (checked) {
      this.addonsValue = [...this.addonsValue, addon]
    } else {
      this.addonsValue = this.addonsValue.filter(a => a !== addon)
    }

    this.updateTotalPrice()
  }

  // Step 3: Calendar
  selectDate(event) {
    event.preventDefault()
    const date = event.currentTarget.dataset.date

    if (!date) return

    this.scheduledDateValue = date

    // Highlight selected date
    this.element.querySelectorAll("[data-date]").forEach(el => {
      el.classList.remove("ring-2", "ring-[#0369a1]", "bg-[#dbeafe]")
    })
    event.currentTarget.classList.add("ring-2", "ring-[#0369a1]", "bg-[#dbeafe]")

    // Load time slots for this date
    this.loadTimeSlots(date)
  }

  async loadTimeSlots(date) {
    if (!this.hasTimeSlotContainerTarget) return

    this.timeSlotContainerTarget.innerHTML = '<div class="text-center py-8"><div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#0369a1]"></div><p class="mt-2 text-slate-500">Loading times...</p></div>'

    try {
      const response = await fetch(`/appointment_request/time_slots?date=${date}`, {
        headers: { 'Accept': 'application/json' }
      })
      const slots = await response.json()

      // Render the slots as HTML
      this.renderTimeSlots(slots, date)
    } catch (error) {
      console.error('Error loading time slots:', error)
      this.timeSlotContainerTarget.innerHTML = '<div class="text-center py-4 text-red-500">Error loading time slots</div>'
    }
  }

  renderTimeSlots(slots, date) {
    if (!Array.isArray(slots) || slots.length === 0) {
      this.timeSlotContainerTarget.innerHTML = '<p class="text-center text-slate-500">No available times for this date</p>'
      return
    }

    let html = '<p class="text-sm font-medium text-slate-700 mb-3">Available Times:</p><div class="grid grid-cols-2 gap-3">'

    slots.forEach(slot => {
      html += `
        <button type="button"
                data-action="click->quote-wizard#selectTimeSlot"
                data-slot="${slot.key}"
                class="p-4 border-2 border-slate-200 rounded-xl text-center hover:border-[#0369a1] transition focus:border-[#0369a1] focus:bg-[#eff6ff]">
          <span class="font-medium text-slate-700">${slot.label}</span>
        </button>
      `
    })

    html += '</div>'
    this.timeSlotContainerTarget.innerHTML = html
  }

  selectTimeSlot(event) {
    event.preventDefault()
    const slot = event.currentTarget.dataset.slot

    if (!slot) return

    this.timeSlotValue = slot

    // Highlight selected slot
    this.element.querySelectorAll("[data-slot]").forEach(el => {
      el.classList.remove("ring-2", "ring-[#0369a1]", "bg-[#dbeafe]")
    })
    event.currentTarget.classList.add("ring-2", "ring-[#0369a1]", "bg-[#dbeafe]")

    // Enable next button
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.disabled = false
    }
  }

  // Navigation
  nextStep(event) {
    if (event) event.preventDefault()

    if (this.validateCurrentStep()) {
      this.stepValue++
      this.updateStepDisplay()
    }
  }

  prevStep(event) {
    if (event) event.preventDefault()

    if (this.stepValue > 1) {
      this.stepValue--
      this.updateStepDisplay()
    }
  }

  goToStep(event) {
    const step = parseInt(event.currentTarget.dataset.step)
    if (step && step <= this.stepValue) {
      this.stepValue = step
      this.updateStepDisplay()
    }
  }

  validateCurrentStep() {
    switch (this.stepValue) {
      case 1:
        if (!this.dwellingTypeValue || !this.squareFootageValue) {
          alert("Please select both property type and square footage")
          return false
        }
        return true
      case 2:
        return true // Addons are optional
      case 3:
        if (!this.scheduledDateValue || !this.timeSlotValue) {
          alert("Please select a date and time slot")
          return false
        }
        return true
      case 4:
        return this.validatePropertyForm()
      case 5:
        return this.validateCustomerForm()
      default:
        return true
    }
  }

  validatePropertyForm() {
    if (!this.hasPropertyFormTarget) return true

    const required = this.propertyFormTarget.querySelectorAll("[required]")
    for (const field of required) {
      if (!field.value.trim()) {
        field.focus()
        alert("Please fill in all required fields")
        return false
      }
    }
    return true
  }

  validateCustomerForm() {
    if (!this.hasCustomerFormTarget) return true

    const required = this.customerFormTarget.querySelectorAll("[required]")
    for (const field of required) {
      if (!field.value.trim()) {
        field.focus()
        alert("Please fill in all required fields")
        return false
      }
    }

    // Validate email format
    const emailField = this.customerFormTarget.querySelector("[type='email']")
    if (emailField && !emailField.value.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      emailField.focus()
      alert("Please enter a valid email address")
      return false
    }

    return true
  }

  updateStepDisplay() {
    // Hide all steps
    if (this.hasStepTarget) {
      this.stepTargets.forEach((step, index) => {
        if (index + 1 === this.stepValue) {
          step.classList.remove("hidden")
        } else {
          step.classList.add("hidden")
        }
      })
    }

    // Update step indicators
    if (this.hasStepIndicatorTarget) {
      this.stepIndicatorTargets.forEach((indicator, index) => {
        const stepNum = index + 1

        // Remove all color classes and inline styles
        indicator.classList.remove("bg-slate-100", "text-slate-400", "text-white")
        indicator.removeAttribute('style')

        if (stepNum < this.stepValue) {
          // Completed - blue background with white text
          indicator.style.backgroundColor = '#0369a1'
          indicator.style.color = 'white'
        } else if (stepNum === this.stepValue) {
          // Current - light blue background with blue text
          indicator.style.backgroundColor = '#dbeafe'
          indicator.style.color = '#0369a1'
        } else {
          // Future - gray
          indicator.classList.add("bg-slate-100", "text-slate-400")
        }
      })
    }

    // Update summary if on step 6
    if (this.stepValue === 6) {
      this.updateSummary()
    }
  }

  updateSummary() {
    if (!this.hasSummaryContentTarget) return

    const dwellingLabels = {
      condo_townhouse: "Condo / Townhouse",
      single_family: "Single Family Home",
      duplex: "Duplex",
      multi_family: "Multi-Family (3+ units)"
    }

    const slotLabels = {
      morning: "9:00 AM - 12:00 PM",
      afternoon: "2:00 PM - 5:00 PM"
    }

    // Gather form data
    const propertyData = this.hasPropertyFormTarget ? new FormData(this.propertyFormTarget) : new FormData()
    const customerData = this.hasCustomerFormTarget ? new FormData(this.customerFormTarget) : new FormData()

    this.summaryContentTarget.innerHTML = `
      <div class="space-y-6">
        <div class="bg-slate-50 rounded-lg p-4">
          <h4 class="font-semibold text-slate-900 mb-2">Property Type</h4>
          <p class="text-slate-600">${dwellingLabels[this.dwellingTypeValue] || this.dwellingTypeValue}</p>
          <p class="text-slate-600">${this.squareFootageValue.toLocaleString()} sq ft</p>
        </div>

        <div class="bg-slate-50 rounded-lg p-4">
          <h4 class="font-semibold text-slate-900 mb-2">Services</h4>
          <ul class="text-slate-600 space-y-1">
            <li>Standard Home Inspection</li>
            ${this.addonsValue.includes('radon_testing') ? '<li>Radon Testing (FREE)</li>' : ''}
            ${this.addonsValue.includes('outbuilding') ? '<li>Outbuilding Inspection (+$125)</li>' : ''}
          </ul>
        </div>

        <div class="bg-slate-50 rounded-lg p-4">
          <h4 class="font-semibold text-slate-900 mb-2">Appointment</h4>
          <p class="text-slate-600">${this.scheduledDateValue}</p>
          <p class="text-slate-600">${slotLabels[this.timeSlotValue] || this.timeSlotValue}</p>
        </div>

        <div class="bg-slate-50 rounded-lg p-4">
          <h4 class="font-semibold text-slate-900 mb-2">Property Address</h4>
          <p class="text-slate-600">${propertyData.get('address') || ''}</p>
          <p class="text-slate-600">${propertyData.get('city') || ''}, ${propertyData.get('state') || ''} ${propertyData.get('zip') || ''}</p>
        </div>

        <div class="bg-slate-50 rounded-lg p-4">
          <h4 class="font-semibold text-slate-900 mb-2">Contact Information</h4>
          <p class="text-slate-600">${customerData.get('first_name') || ''} ${customerData.get('last_name') || ''}</p>
          <p class="text-slate-600">${customerData.get('email') || ''}</p>
          <p class="text-slate-600">${customerData.get('phone') || ''}</p>
        </div>

        <div class="bg-[#eff6ff] rounded-lg p-4 border-2 border-[#93c5fd]">
          <div class="flex justify-between items-center">
            <span class="text-lg font-semibold text-slate-900">Total</span>
            <span class="text-2xl font-bold text-[#0369a1]">$${this.totalPriceValue}</span>
          </div>
        </div>
      </div>
    `
  }

  // Submit to checkout
  async proceedToCheckout(event) {
    event.preventDefault()

    const propertyData = this.hasPropertyFormTarget ? Object.fromEntries(new FormData(this.propertyFormTarget)) : {}
    const customerData = this.hasCustomerFormTarget ? Object.fromEntries(new FormData(this.customerFormTarget)) : {}

    const bookingData = {
      dwelling_type: this.dwellingTypeValue,
      square_footage: this.squareFootageValue,
      selected_services: this.addonsValue,
      scheduled_date: this.scheduledDateValue,
      time_slot: this.timeSlotValue,
      property_info: propertyData,
      personal_info: customerData,
      email: customerData.email
    }

    // Generate idempotency key to prevent double-submissions
    const idempotencyKey = `booking_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`

    try {
      const response = await fetch("/appointment_request", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
        },
        body: JSON.stringify({ appointment_request: bookingData, idempotency_key: idempotencyKey })
      })

      const result = await response.json()

      if (result.success) {
        window.location.href = result.checkout_url
      } else {
        alert(result.error || "Something went wrong. Please try again.")
      }
    } catch (error) {
      console.error("Checkout error:", error)
      alert("Something went wrong. Please try again.")
    }
  }
}
